import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,            // send session cookie cross-origin
});

// Auth (Phase B.5)
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authExchangeSession = (session_id) =>
  api.post("/auth/session", { session_id }).then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const authConfig = () => api.get("/auth/config").then((r) => r.data);

// My Tastings (per-user, drives host/lead_host landing page)
export const fetchMyTastings = () => api.get("/me/tastings").then((r) => r.data);

// Admin users page (admin role only)
export const fetchUsers = () => api.get("/admin/users").then((r) => r.data);
export const inviteUser = (payload) =>
  api.post("/admin/users", payload).then((r) => r.data);
export const patchUser = (id, payload) =>
  api.patch(`/admin/users/${id}`, payload).then((r) => r.data);
export const removeUser = (id) =>
  api.delete(`/admin/users/${id}`).then((r) => r.data);
export const fetchAuditLog = (limit = 200) =>
  api.get("/admin/audit-log", { params: { limit } }).then((r) => r.data);
export const fetchTeamActivity = (days = 7) =>
  api.get("/admin/team-activity", { params: { days } }).then((r) => r.data);
export const unassignAllDispensaries = () =>
  api.post("/admin/unassign-all-dispensaries").then((r) => r.data);

export const fetchDispensaries = (params = {}) =>
  api.get("/dispensaries", { params }).then((r) => r.data);

export const fetchDispensary = (id) =>
  api.get(`/dispensaries/${id}`).then((r) => r.data);

export const updateDispensary = (id, payload) =>
  api.patch(`/dispensaries/${id}`, payload).then((r) => r.data);

// Admin/dispatcher — bulk-assign N dispensaries to one rep (or unassign).
// `userId` may be null/"unassigned" to clear ownership.
export const bulkAssignDispensaries = (dispensaryIds, userId) =>
  api
    .post(`/dispensaries/bulk-assign`, {
      dispensary_ids: dispensaryIds,
      user_id: userId || "unassigned",
    })
    .then((r) => r.data);

// Bus tastings — dispatcher/admin can spawn N tastings in one shot
// from a hand-picked dispensary multi-select. Per-stop time slots,
// one shared date.
export const bulkRequestBusTastings = (payload) =>
  api.post(`/bus-tastings/bulk-request`, payload).then((r) => r.data);

// --- Weekly Recap (admin-only) ---
export const fetchRecap = (params = {}) =>
  api.get("/recap", { params }).then((r) => r.data);

// --- Dispensary search-by-address + assignment requests ---
export const searchDispensaryByAddress = (query) =>
  api.post("/dispensaries/search-by-address", { query }).then((r) => r.data);
export const requestDispensaryAssignment = (dispensaryId, note) =>
  api
    .post(`/dispensaries/${dispensaryId}/request-assignment`, { note })
    .then((r) => r.data);
export const fetchMyAssignmentRequests = () =>
  api.get("/dispensaries/my-requests").then((r) => r.data);
export const fetchPendingAssignmentRequests = (status = "pending") =>
  api.get("/admin/assignment-requests", { params: { status } }).then((r) => r.data);
export const approveAssignmentRequest = (reqId) =>
  api.post(`/admin/assignment-requests/${reqId}/approve`).then((r) => r.data);
export const rejectAssignmentRequest = (reqId, reason) =>
  api
    .post(`/admin/assignment-requests/${reqId}/reject`, { reason })
    .then((r) => r.data);

// --- Partners ---
export const fetchPartners = (params = {}) =>
  api.get("/partners", { params }).then((r) => r.data);
export const fetchPartner = (id) => api.get(`/partners/${id}`).then((r) => r.data);
export const createPartner = (payload) =>
  api.post("/partners", payload).then((r) => r.data);
export const updatePartner = (id, payload) =>
  api.patch(`/partners/${id}`, payload).then((r) => r.data);
export const approvePartner = (id) =>
  api.post(`/partners/${id}/approve`).then((r) => r.data);
export const rejectPartner = (id, reason) =>
  api.post(`/partners/${id}/reject`, { reason }).then((r) => r.data);
export const activatePartner = (id) =>
  api.post(`/partners/${id}/activate`).then((r) => r.data);
export const archivePartner = (id) =>
  api.post(`/partners/${id}/archive`).then((r) => r.data);
export const unarchivePartner = (id) =>
  api.post(`/partners/${id}/unarchive`).then((r) => r.data);
export const deletePartner = (id) =>
  api.delete(`/partners/${id}`).then((r) => r.data);

// --- Anonymous Tasting Feedback ---
// Admin/dispatcher pulls the QR + public URL to drop into the route
// email or load onto the picture frame at the bus exit. We pass the
// current window.origin so the QR encodes a URL that buyers can
// actually open from outside the K8s cluster (proxies rewrite
// Origin/Referer headers so the backend can't infer it).
export const fetchTastingFeedbackLink = (tastingId) =>
  api
    .get(`/bus-tastings/${tastingId}/feedback-link`, {
      params: { base_url: window.location.origin },
    })
    .then((r) => r.data);

// Admin inbox of all submitted feedback.
export const fetchAdminFeedback = (params = {}) =>
  api.get(`/admin/tasting-feedback`, { params }).then((r) => r.data);

// Admin marks reviewed / edits tagged users / deletes spam.
export const updateAdminFeedback = (feedbackId, payload) =>
  api.patch(`/admin/tasting-feedback/${feedbackId}`, payload).then((r) => r.data);

export const deleteAdminFeedback = (feedbackId) =>
  api.delete(`/admin/tasting-feedback/${feedbackId}`).then((r) => r.data);

// Public endpoints — no auth, no cookies sent. Use a bare axios call
// so we don't accidentally surface the session_token cookie.
const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: false,
});
export const fetchPublicTastingInfo = (tastingId) =>
  publicApi.get(`/public/tasting-feedback/${tastingId}`).then((r) => r.data);
export const submitPublicFeedback = (tastingId, payload) =>
  publicApi.post(`/public/tasting-feedback/${tastingId}`, payload).then((r) => r.data);

export const fetchStats = () => api.get("/stats").then((r) => r.data);
export const fetchFacets = () => api.get("/facets").then((r) => r.data);
export const fetchScans = () => api.get("/scans").then((r) => r.data);

export const runScan = (targetIds) =>
  api
    .post("/scan/run", null, { params: targetIds ? { target_ids: targetIds } : {} })
    .then((r) => r.data);

export const getScan = (id) => api.get(`/scan/${id}`).then((r) => r.data);

export const fetchSettings = () => api.get("/settings").then((r) => r.data);
export const updateSettings = (payload) =>
  api.put("/settings", payload).then((r) => r.data);

export const exportCSVUrl = () => `${API}/export/csv`;
export const exportJSONUrl = () => `${API}/export/json`;

// Snapshots
export const fetchSnapshots = () => api.get("/snapshots").then((r) => r.data);
export const fetchSnapshot = (id) =>
  api.get(`/snapshots/${id}`).then((r) => r.data);
export const createSnapshot = (payload) =>
  api.post("/snapshots", payload).then((r) => r.data);
export const deleteSnapshot = (id) =>
  api.delete(`/snapshots/${id}`).then((r) => r.data);

// Map
export const fetchMapDispensaries = (params = {}) =>
  api.get("/dispensaries/map", { params }).then((r) => r.data);

// Bootstrap
export const fetchBootstrapStatus = () =>
  api.get("/bootstrap/status").then((r) => r.data);

export const fetchDiscoveryStats = () =>
  api.get("/discovery/stats").then((r) => r.data);

// Dispensaries fetched in a specific order (used by /map?ids handoff)
export const fetchDispensariesByIds = (ids) =>
  api.post("/dispensaries/by-ids", { ids }).then((r) => r.data);

// People who work AT a given dispensary (per-account CRM contacts)
export const fetchDispensaryContacts = (dispensaryId) =>
  api.get(`/dispensaries/${dispensaryId}/contacts`).then((r) => r.data);
export const createDispensaryContact = (dispensaryId, payload) =>
  api.post(`/dispensaries/${dispensaryId}/contacts`, payload).then((r) => r.data);
export const updateDispensaryContact = (contactId, payload) =>
  api.put(`/dispensary-contacts/${contactId}`, payload).then((r) => r.data);
export const deleteDispensaryContact = (contactId) =>
  api.delete(`/dispensary-contacts/${contactId}`).then((r) => r.data);

// Interactions — every touchpoint logged against a dispensary account
export const fetchInteractions = (dispensaryId) =>
  api.get(`/dispensaries/${dispensaryId}/interactions`).then((r) => r.data);
export const createInteraction = (dispensaryId, payload) =>
  api.post(`/dispensaries/${dispensaryId}/interactions`, payload).then((r) => r.data);
export const updateInteraction = (interactionId, payload) =>
  api.put(`/interactions/${interactionId}`, payload).then((r) => r.data);
export const deleteInteraction = (interactionId) =>
  api.delete(`/interactions/${interactionId}`).then((r) => r.data);
export const fetchRecentInteractions = (limit = 10) =>
  api.get(`/interactions/recent`, { params: { limit } }).then((r) => r.data);
export const fetchInteractionStats = () =>
  api.get(`/interactions/stats`).then((r) => r.data);

// Sales notes — append-only log per dispensary
export const fetchSalesNotes = (dispensaryId) =>
  api.get(`/dispensaries/${dispensaryId}/sales-notes`).then((r) => r.data);
export const createSalesNote = (dispensaryId, body) =>
  api
    .post(`/dispensaries/${dispensaryId}/sales-notes`, { body })
    .then((r) => r.data);
export const deleteSalesNote = (noteId) =>
  api.delete(`/sales-notes/${noteId}`).then((r) => r.data);

// Tasks — per-account checklist of forward-looking to-dos (Phase E)
export const fetchTasks = (dispensaryId) =>
  api.get(`/dispensaries/${dispensaryId}/tasks`).then((r) => r.data);
export const createTask = (dispensaryId, payload) =>
  api.post(`/dispensaries/${dispensaryId}/tasks`, payload).then((r) => r.data);
export const updateTask = (taskId, payload) =>
  api.put(`/tasks/${taskId}`, payload).then((r) => r.data);
export const deleteTask = (taskId) =>
  api.delete(`/tasks/${taskId}`).then((r) => r.data);
export const fetchMyTasks = (params = {}) =>
  api.get(`/tasks/my`, { params }).then((r) => r.data);
export const fetchAssignedByMeTasks = (params = {}) =>
  api.get(`/tasks/assigned-by-me`, { params }).then((r) => r.data);
export const bulkAssignTasks = (payload) =>
  api.post(`/tasks/bulk-assign`, payload).then((r) => r.data);

// CRM pipeline (Phase D)
export const fetchPipelineSummary = (assignedToId) =>
  api
    .get(`/pipeline/summary`, {
      params: assignedToId ? { assigned_to_id: assignedToId } : {},
    })
    .then((r) => r.data);

// Bus tastings — events outside the CRM pipeline
export const fetchBusTastings = (params = {}) =>
  api.get(`/bus-tastings`, { params }).then((r) => r.data);
export const fetchBusTastingStats = () =>
  api.get(`/bus-tastings/stats`).then((r) => r.data);
export const fetchBusTastingsForDispensary = (dispensaryId) =>
  api.get(`/dispensaries/${dispensaryId}/bus-tastings`).then((r) => r.data);
export const requestBusTasting = (dispensaryId, payload) =>
  api.post(`/dispensaries/${dispensaryId}/bus-tastings`, payload).then((r) => r.data);
export const scheduleBusTasting = (tastingId, payload) =>
  api.post(`/bus-tastings/${tastingId}/schedule`, payload).then((r) => r.data);
export const computeRouteTimeline = (payload) =>
  api.post(`/routes/compute-timeline`, payload).then((r) => r.data);
export const fetchSalesScorecard = () =>
  api.get(`/reports/sales-scorecard`).then((r) => r.data);
export const createTrip = (payload) =>
  api.post(`/trips`, payload).then((r) => r.data);
export const fetchTrip = (tripId) =>
  api.get(`/trips/${tripId}`).then((r) => r.data);
export const cancelTrip = (tripId) =>
  api.delete(`/trips/${tripId}`).then((r) => r.data);
export const uncancelTrip = (tripId) =>
  api.post(`/trips/${tripId}/uncancel`).then((r) => r.data);
export const updateBusTasting = (tastingId, payload) =>
  api.put(`/bus-tastings/${tastingId}`, payload).then((r) => r.data);
export const completeBusTasting = (tastingId) =>
  api.post(`/bus-tastings/${tastingId}/complete`).then((r) => r.data);
export const cancelBusTasting = (tastingId) =>
  api.post(`/bus-tastings/${tastingId}/cancel`).then((r) => r.data);

// Contacts (sales reps + bus drivers) for SMS deep-links
export const fetchContacts = () =>
  api.get("/contacts").then((r) => r.data);
export const createContact = (payload) =>
  api.post("/contacts", payload).then((r) => r.data);
export const updateContact = (id, payload) =>
  api.put(`/contacts/${id}`, payload).then((r) => r.data);
export const deleteContact = (id) =>
  api.delete(`/contacts/${id}`).then((r) => r.data);

// Nominatim geocoding (free, OSM)
export const geocodeAddress = (q) =>
  api.get("/geocode", { params: { q } }).then((r) => r.data);

export const rescanDispensary = (id) =>
  api.post(`/dispensaries/${id}/rescan`).then((r) => r.data);

export const runFullScan = () =>
  api.post("/scan/run", null, { params: { full: true } }).then((r) => r.data);

// Partner priorities (sponsor brand → dispensary pairings).
export const fetchPartnerPriorities = (partnerId) =>
  api.get(`/partners/${partnerId}/priorities`).then((r) => r.data);
export const addPartnerPriority = (partnerId, payload) =>
  api.post(`/partners/${partnerId}/priorities`, payload).then((r) => r.data);
export const updatePartnerPriority = (partnerId, dispensaryId, payload) =>
  api
    .patch(`/partners/${partnerId}/priorities/${dispensaryId}`, payload)
    .then((r) => r.data);
export const removePartnerPriority = (partnerId, dispensaryId) =>
  api
    .delete(`/partners/${partnerId}/priorities/${dispensaryId}`)
    .then((r) => r.data);
export const previewBulkImportPriorities = (partnerId, rawText) =>
  api
    .post(`/partners/${partnerId}/priorities/bulk-import/preview`, {
      raw_text: rawText,
    })
    .then((r) => r.data);
export const confirmBulkImportPriorities = (partnerId, dispensaryIds, partnerNotes) =>
  api
    .post(`/partners/${partnerId}/priorities/bulk-import/confirm`, {
      dispensary_ids: dispensaryIds,
      partner_notes: partnerNotes || "",
    })
    .then((r) => r.data);
export const fetchDispensaryPriorityPartners = (dispensaryId) =>
  api
    .get(`/dispensaries/${dispensaryId}/priority-partners`)
    .then((r) => r.data);

// 10-tone brand palette — keep in sync with backend PARTNER_BRAND_PALETTE.
export const PARTNER_BRAND_PALETTE = [
  { hex: "#10b981", name: "Emerald" },
  { hex: "#f59e0b", name: "Amber" },
  { hex: "#8b5cf6", name: "Violet" },
  { hex: "#06b6d4", name: "Cyan" },
  { hex: "#f43f5e", name: "Rose" },
  { hex: "#f97316", name: "Orange" },
  { hex: "#0ea5e9", name: "Sky" },
  { hex: "#84cc16", name: "Lime" },
  { hex: "#d946ef", name: "Fuchsia" },
  { hex: "#64748b", name: "Slate" },
];

// Returns 1-2 upper-case initials derived from a partner's company name.
// "Flora Farms" → "FF", "Jeeter" → "JE". Falls back to "?" if blank.
export const partnerInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  const only = parts[0] || "";
  return (only.slice(0, 2)).toUpperCase() || "?";
};
